import { NavLink } from 'react-router-dom';
import { Bell, Building2, ClipboardList, IndianRupee, Target, User } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/app/store/authStore';
import { canCreateSalesOrders, canManageLeads } from '@/app/lib/permissions';
import { useUnreadAlerts } from '@/app/hooks/useUnreadAlerts';

interface TabItem {
    to: string;
    icon: typeof Bell;
    label: string;
}

// Ordered as the funnel actually runs: prospect -> customer -> order -> money.
//
// Alerts deliberately has no tab — it is reached from Profile. The Profile tab
// carries the unread badge so a pending OTP approval is still visible at a glance.
const tabs: TabItem[] = [
    { to: '/leads', icon: Target, label: 'Leads' },
    { to: '/parties', icon: Building2, label: 'Parties' },
    { to: '/sales-orders', icon: ClipboardList, label: 'Orders' },
    { to: '/payments-in/new', icon: IndianRupee, label: 'Payment' },
    { to: '/profile', icon: User, label: 'Profile' },
];

export function BottomTabBar() {
    const user = useAuthStore((state) => state.user);
    const unreadAlerts = useUnreadAlerts();
    // Hide tabs the backend would reject anyway (e.g. field_agent on orders/leads),
    // rather than letting the user walk into a 403.
    const visibleTabs = tabs.filter((tab) => {
        if (tab.to === '/sales-orders') return canCreateSalesOrders(user);
        if (tab.to === '/leads') return canManageLeads(user);
        return true;
    });

    return (
        <nav className="bottom-tab-bar">
            <div className="flex items-center justify-around h-14">
                {visibleTabs.map(({ to, icon: Icon, label }) => (
                    <NavLink
                        key={to}
                        to={to}
                        className={({ isActive }) =>
                            cn(
                                'flex flex-col items-center justify-center flex-1 h-full py-2 transition-colors',
                                isActive
                                    ? 'text-primary'
                                    : 'text-muted-foreground'
                            )
                        }
                    >
                        {({ isActive }) => (
                            <>
                                <div
                                    className={cn(
                                        'relative flex items-center justify-center w-12 h-7 rounded-full transition-colors',
                                        isActive && 'bg-primary/10'
                                    )}
                                >
                                    <Icon
                                        className={cn(
                                            'h-5 w-5',
                                            isActive && 'text-primary'
                                        )}
                                        strokeWidth={isActive ? 2.5 : 2}
                                    />
                                    {/* Alerts live under Profile now, so the unread
                                        count surfaces here instead of on its own tab. */}
                                    {to === '/profile' && unreadAlerts > 0 && (
                                        <span className="absolute -top-0.5 right-1.5 min-w-[16px] h-4 px-1 rounded-full bg-destructive text-destructive-foreground text-[10px] font-semibold flex items-center justify-center">
                                            {unreadAlerts > 9 ? '9+' : unreadAlerts}
                                        </span>
                                    )}
                                </div>
                                <span
                                    className={cn(
                                        'text-[10px] mt-1 font-medium',
                                        isActive && 'text-primary'
                                    )}
                                >
                                    {label}
                                </span>
                            </>
                        )}
                    </NavLink>
                ))}
            </div>
        </nav>
    );
}
